// Funnel event tracking, fanned out to GA4, Google Ads and Meta Pixel.
// Purchases are tracked by trackPurchase() at the bottom of this file.

// Google Ads conversion labels, keyed by funnel event. Only real labels go
// here; an event with no entry simply sends no Ads conversion.
const ADS = {};

// "Order placed": the Primary purchase conversion in Google Ads. Sent only by
// trackPurchase() below, after the server has verified a Razorpay payment.
const ORDER_PLACED = 'AW-17963167817/tkHvCLjvlI8dEMngwPVC';

export const FUNNEL = {
  PRICE_PAGE_VIEW: 'price_page_view',
  PRODUCT_CLICK: 'product_click',
  PRODUCT_VIEW: 'product_view',
  COLLECTION_VIEW: 'collection_view',
  SERIES_EXPAND: 'series_expand',
  AGENT_OPEN: 'agent_open',
  AGENT_MESSAGE: 'agent_message',
  SIZING_COMPLETE: 'sizing_complete',
  QUOTE_STARTED: 'quote_started',
  QUOTE_STEP: 'quote_step',
  KIT_CHOSEN: 'kit_chosen',
  ADD_TO_CART: 'add_to_cart',
  CART_VIEW: 'cart_view',
  CART_CLEARED: 'cart_cleared',
  KIT_ADDON_TOGGLED: 'kit_addon_toggled',
  CHECKOUT_STARTED: 'checkout_started',
  PINCODE_CHECKED: 'pincode_checked',
  QUOTE_DOWNLOADED: 'quote_downloaded',
  INSTALL_TOGGLED: 'install_toggled',
  QUOTE_GENERATED: 'quote_generated',
  WHATSAPP_HANDOFF: 'whatsapp_handoff',
  TOKEN_STARTED: 'token_started',
  PHONE_CLICK: 'phone_click',
  CONSULT_STARTED: 'consult_started',
  CONSULT_PAID: 'consult_paid',
};

// ─── Enhanced conversions (in-page code) ──────────────────────────────────
//
// Google Ads matches a conversion to the ad click more reliably when the page
// hands over what the customer typed: mobile, email, name, pincode. gtag
// hashes these in the browser (SHA-256) before anything leaves the page.
//
// Called from checkout as soon as a valid mobile and serviceable pincode are
// entered, and again on submit. Every Google Ads conversion that fires after
// that on the same page (Begin checkout, quote generated, WhatsApp) carries
// the data.
//
// Formats Google requires:
//   phone_number  E.164, so "+91" + 10 digits
//   email         lower case, trimmed
//   address       first_name, last_name, postal_code and country go together;
//                 sent only when the customer gave both a first and last name.
//
// Never throws. Tracking must not break checkout.
export function setAdsUserData(form = {}, pin = null) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    const data = {};

    const mobile = String(form.mobile || '').replace(/\D/g, '').slice(-10);
    if (/^[6-9]\d{9}$/.test(mobile)) data.phone_number = `+91${mobile}`;

    const email = String(form.email || '').trim().toLowerCase();
    if (/^\S+@\S+\.\S+$/.test(email)) data.email = email;

    const parts = String(form.fullName || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    const postal = String(form.pincode || '').trim();
    if (parts.length >= 2 && /^\d{6}$/.test(postal)) {
      data.address = {
        first_name: parts[0],
        last_name: parts.slice(1).join(' '),
        postal_code: postal,
        country: 'IN',
        ...(pin?.city ? { city: String(pin.city).toLowerCase() } : {}),
      };
    }

    if (!data.phone_number && !data.email) return;
    window.gtag('set', 'user_data', data);
  } catch { /* never break checkout for tracking */ }
}

// ─── Run analytics after the screen updates ────────────────────────────────
//
// Google's INP metric times every tap until the page visibly responds. gtag,
// the Meta Pixel and Tag Manager all do work on each event, and running them
// inside the tap made "Add to cart", filters and every tracked button wait
// for them first. Now the tap paints, then the event is sent.
//
// Sent within 150ms even in a background tab (where animation frames pause),
// and flushed if the page is closed or left before then, so nothing is lost.
const pendingEvents = new Set();

function afterPaint(fn) {
  if (typeof window === 'undefined') return;
  const run = () => {
    if (!pendingEvents.delete(run)) return;
    try { fn(); } catch { /* tracking must never break the page */ }
  };
  pendingEvents.add(run);
  setTimeout(run, 150);
  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => setTimeout(run, 0));
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { [...pendingEvents].forEach((run) => run()); });
}

export function track(event, params = {}) {
  if (typeof window === 'undefined') return;
  afterPaint(() => sendNow(event, params));
}

function sendNow(event, params = {}) {
  if (typeof window === 'undefined') return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', event, params);
    const sendTo = ADS[event];
    if (sendTo && !sendTo.includes('REPLACE_')) {
      window.gtag('event', 'conversion', {
        send_to: sendTo,
        value: params.value || 0,
        currency: 'INR',
        transaction_id: params.quote_ref || undefined,
      });
    }
  }

  if (typeof window.fbq === 'function') {
    const map = {
      [FUNNEL.ADD_TO_CART]: 'AddToCart',
      [FUNNEL.CHECKOUT_STARTED]: 'InitiateCheckout',
      [FUNNEL.QUOTE_STARTED]: 'InitiateCheckout',
      [FUNNEL.QUOTE_GENERATED]: 'Lead',
      [FUNNEL.WHATSAPP_HANDOFF]: 'Contact',
      [FUNNEL.TOKEN_STARTED]: 'AddPaymentInfo',
      [FUNNEL.PHONE_CLICK]: 'Contact',
      [FUNNEL.PRODUCT_VIEW]: 'ViewContent',
    };
    const std = map[event];
    if (std) {
      window.fbq('track', std, {
        value: params.value || 0,
        currency: 'INR',
        content_name: params.model || params.series || undefined,
      });
    } else {
      window.fbq('trackCustom', event, params);
    }
  }
}

export const trackQuoteGenerated = ({ ref, total, units }) =>
  track(FUNNEL.QUOTE_GENERATED, { quote_ref: ref, value: total, units });

export const trackWhatsAppHandoff = ({ ref, total, source }) =>
  track(FUNNEL.WHATSAPP_HANDOFF, { quote_ref: ref, value: total, source });

// ─── Purchase ──────────────────────────────────────────────────────────────
//
// Fired once, from PayButton, after /api/verify-payment has confirmed the
// Razorpay signature. Never on a quote: a quote is not a sale, and counting
// quotes teaches Google Ads to find people who ask for prices.
//
// One call, three destinations:
//   GA4         'purchase' with the line items (a key event by default)
//   Google Ads  "Order placed", value = order total
//   Meta Pixel  'Purchase'
//
// transaction_id is the CC-... order reference, so Google ignores a repeat
// for the same order. A session flag stops the browser sending one at all.
//
// Value is what the customer paid for the goods and delivery, after any
// exchange credit. Razorpay's processing fee is not revenue and is left out.
export function trackPurchase({ ref, quote, paymentId, customer = {}, pincode } = {}) {
  try {
    if (typeof window === 'undefined' || !quote) return;

    const orderId = String(ref || paymentId || '');
    const flag = `cc_purchase_tracked:${orderId}`;
    try {
      if (orderId && window.sessionStorage.getItem(flag)) return;
      if (orderId) window.sessionStorage.setItem(flag, '1');
    } catch { /* storage off: Google still de-duplicates on transaction_id */ }

    const value = Math.round(Number(quote.total) || 0);
    const items = (quote.lines || []).map((l) => {
      const model = l.model || {};
      const variant = (model.variants && model.variants[l.kit]) || {};
      const qty = Number(l.qty) || 1;
      return {
        item_id: variant.idu || model.id || 'unknown',
        item_name: model.title || variant.idu || 'Daikin AC',
        item_brand: 'Daikin',
        item_variant: l.kit === 'withKit' ? 'With piping kit' : 'Without kit',
        price: Math.round((Number(l.equipment) || 0) / qty),
        quantity: qty,
      };
    });

    // Enhanced conversions: hand Google the customer's details again, in case
    // the checkout screen's earlier call did not run.
    setAdsUserData(
      { mobile: customer.phone, email: customer.email, fullName: customer.name, pincode },
      null,
    );

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'purchase', {
        transaction_id: orderId || undefined,
        value,
        currency: 'INR',
        items,
      });
      window.gtag('event', 'conversion', {
        send_to: ORDER_PLACED,
        value,
        currency: 'INR',
        transaction_id: orderId || undefined,
      });
    }

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Purchase', {
        value,
        currency: 'INR',
        content_type: 'product',
        content_ids: items.map((i) => i.item_id),
        num_items: items.reduce((n, i) => n + i.quantity, 0),
      }, orderId ? { eventID: orderId } : undefined);
    }
  } catch { /* tracking must never break the paid screen */ }
}
